import type { ReactElement, ReactNode } from 'react'
import { ArrowRightIcon } from 'lucide-react'
import { Slot } from 'radix-ui'

import { cn } from 'cn'

import { CardSkeleton, Line } from '#ui/patterns/skeleton-blocks.tsx'

/**
 * What the figure asks of the reader.
 *
 * `attention` is for a queue that somebody has to work, such as registrations that wait on
 * verification. It marks the card with the warning color **and** a dot, so the color is not the
 * only signal. Every other figure is `neutral`.
 */
export type StatTone = 'neutral' | 'attention'

/**
 * One figure, its name and one line about it: "Registrations · 1,204 · 18 wait on verification".
 *
 * The figure uses `tabular-nums`, so the cards in a row have the same digit width and a change in
 * one figure does not move the others.
 *
 * `link` is optional. Give it a link element (a router `Link`, or an `<a>`) with the words of the
 * next step as its children. The card puts the link at its foot and makes the whole card a target
 * for the pointer. The link stays the one focusable element, so the keyboard and a screen reader
 * find one link with a name, not a card and a link.
 */
export function StatCard({
  label,
  value,
  hint,
  icon,
  tone = 'neutral',
  link,
  className,
}: {
  label: ReactNode
  /** Formatted by the caller: a count, an amount, "12 / 400". */
  value: ReactNode
  /** One short line under the figure. */
  hint?: ReactNode
  /** A lucide icon element. Decorative: the label carries the meaning. */
  icon?: ReactNode
  tone?: StatTone
  link?: ReactElement
  className?: string
}) {
  return (
    <div
      data-tone={tone}
      className={cn(
        'relative flex min-w-0 flex-col gap-2 rounded-xl border bg-card p-4 text-card-foreground shadow-xs transition-[border-color] duration-200 ease-out',
        'data-[tone=attention]:border-warning/40',
        link === undefined
          ? undefined
          : // On hover the border takes the brand color, and nothing moves: the card stays in
            // its place, so the row of figures does not shift under the pointer.
            'has-[a:hover]:border-primary/40 has-[a:focus-visible]:ring-[3px] has-[a:focus-visible]:ring-ring/50',
        className,
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <p className="flex min-w-0 items-center gap-2 text-sm font-medium text-muted-foreground">
          {tone === 'attention' ? (
            <span aria-hidden="true" className="size-2 shrink-0 rounded-full bg-warning" />
          ) : null}
          <span className="truncate">{label}</span>
        </p>
        {icon === undefined ? null : (
          <span
            aria-hidden="true"
            className="text-muted-foreground [&_svg]:size-4 [&_svg]:shrink-0"
          >
            {icon}
          </span>
        )}
      </div>
      <p className="text-2xl font-semibold tracking-tight tabular-nums">{value}</p>
      {hint === undefined ? null : <p className="text-xs text-muted-foreground">{hint}</p>}
      {link === undefined ? null : (
        <Slot.Root
          className={cn(
            'mt-auto inline-flex w-fit items-center gap-1 pt-1 text-sm font-medium text-primary outline-none',
            // The link's hit area covers the card. The card is `relative`, so this stops at its edge.
            'after:absolute after:inset-0 after:rounded-xl',
            '[&_svg]:size-3.5',
          )}
        >
          {link}
        </Slot.Root>
      )}
    </div>
  )
}

/** The arrow that ends a `StatCard` link's words. Put it after the text, inside the link. */
export function StatCardArrow() {
  return <ArrowRightIcon aria-hidden="true" />
}

/** A `StatCard` before its figure arrives. The same height, so the row does not jump. */
export function StatCardSkeleton({
  link = true,
  className,
}: {
  /** Leave room for the link at the foot. */
  link?: boolean
  className?: string
}) {
  return (
    <CardSkeleton className={cn('gap-2 p-4', className)}>
      <Line width="w-28" />
      <Line size="2xl" width="w-20" />
      <Line size="xs" width="w-36" />
      {link ? <Line width="w-24" className="mt-auto pt-1" /> : null}
    </CardSkeleton>
  )
}
